// The instanced quad batch: every sprite layer of the map is one of these
// (the plan's A.2 and A.4).
//
// One `Float32Array`, one instance buffer, and four corners read from the
// shared per-vertex buffer of `gl/corner.ts`. A whole layer costs one upload
// and one draw call. Instance order inside a batch is paint order, so a
// layer packs its sub-layers into one batch in z-order.
//
// The batch is BACKEND-NEUTRAL: it writes floats, sets uniforms by name and
// asks the instance buffer to draw. The buffer and the pipeline are the
// halves each backend implements, so this file is written once.

import type { AttribSpec, BatchDeps, Pipeline } from './types';
import { InstanceBatch } from './batch';
import { CORNER_ATTRIB, CORNER_DECODE_GLSL } from '../gl/corner';
import type { ProgramSource } from '../shaders/program';
import type { UniformSpec } from '../shaders/prelude';
import { quadModuleWgsl } from '../shaders/wgsl/quads';
import type { AtlasEntry } from './atlas';
import type { CameraUniforms } from '../layer';

/** 16 floats, 64 bytes (the plan's A.4). */
export const QUAD_FLOATS = 16;

export const QUAD_ATTRIBS: readonly AttribSpec[] = [
  { name: 'aPos', location: 1, size: 2, offset: 0 },      // world pivot
  { name: 'aOffset', location: 2, size: 2, offset: 2 },   // box centre from the pivot
  { name: 'aSize', location: 3, size: 2, offset: 4 },
  { name: 'aRot', location: 4, size: 1, offset: 6 },      // radians, +x nose, y down
  { name: 'aFlags', location: 5, size: 1, offset: 7 },
  { name: 'aUV', location: 6, size: 4, offset: 8 },       // u0 v0 u1 v1
  { name: 'aColor', location: 7, size: 4, offset: 12 },   // straight rgba
];

/**
 * The batch's uniform block, in order.
 *
 * WebGL2 looks every name up and needs no list. WebGPU packs the block into
 * one buffer at byte offsets, so the ORDER is the contract and it is stated
 * here. `uViewProj` is the camera matrix, which the flush writes.
 */
export const QUAD_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uViewProj', type: 'mat3', value: [1, 0, 0, 0, 1, 0, 0, 0, 1], dynamic: true },
  { name: 'uZoom', type: 'float', value: 1, dynamic: true },
  { name: 'uAlpha', type: 'float', value: 1, dynamic: true },
  { name: 'uStep', type: 'vec4', value: [1, 1, 1, 1], dynamic: true, note: 'the band scale of the four chart text steps (R1796)' },
  { name: 'uTex', type: 'sampler', value: 0, unit: 0, note: 'the sprite atlas' },
];

/**
 * Writes one quad instance. Pure, so a test pins the byte layout without a
 * GPU. `color` is a 24-bit rgb number and `alpha` is straight.
 */
export function writeQuad(
  out: Float32Array, at: number,
  x: number, y: number, ox: number, oy: number,
  w: number, h: number, rot: number, flags: number,
  u0: number, v0: number, u1: number, v1: number,
  color: number, alpha: number,
): void {
  out[at] = x; out[at + 1] = y;
  out[at + 2] = ox; out[at + 3] = oy;
  out[at + 4] = w; out[at + 5] = h;
  out[at + 6] = rot; out[at + 7] = flags;
  out[at + 8] = u0; out[at + 9] = v0; out[at + 10] = u1; out[at + 11] = v1;
  out[at + 12] = ((color >> 16) & 0xff) / 255;
  out[at + 13] = ((color >> 8) & 0xff) / 255;
  out[at + 14] = (color & 0xff) / 255;
  out[at + 15] = alpha;
}

// ---------------------------------------------------------------------------
// The shaders
// ---------------------------------------------------------------------------

export const QUAD_VERTEX = `#version 300 es
layout(location=0) in float aCorner;
layout(location=1) in vec2 aPos;
layout(location=2) in vec2 aOffset;
layout(location=3) in vec2 aSize;
layout(location=4) in float aRot;
layout(location=5) in float aFlags;
layout(location=6) in vec4 aUV;
layout(location=7) in vec4 aColor;
uniform mat3 uViewProj;
uniform float uZoom;
uniform vec4 uStep;
out vec2 vUV;
out vec4 vColor;
void main() {
${CORNER_DECODE_GLSL}
  int flags = int(aFlags + 0.5);
  int st = (flags >> 3) & 7;                           // F_STEP_SHIFT (R1796)
  float ks = st == 1 ? uStep.x : st == 2 ? uStep.y : st == 3 ? uStep.z : st == 4 ? uStep.w : 1.0;
  float k = (((flags & 1) != 0) ? 1.0 / uZoom : 1.0) * ks;   // F_SCREEN
  vec2 local = ((corner - 0.5) * aSize + aOffset) * k;
  float c = cos(aRot), s = sin(aRot);
  vec2 world = aPos + vec2(local.x * c - local.y * s, local.x * s + local.y * c);
  gl_Position = vec4((uViewProj * vec3(world, 1.0)).xy, 0.0, 1.0);
  vUV = mix(aUV.xy, aUV.zw, corner);
  float a = ((flags & 2) != 0) ? 0.0 : aColor.a;      // F_ADD
  vColor = vec4(aColor.rgb * aColor.a, a);
}`;

// The atlas texel is premultiplied and so is vColor.rgb, so the product is
// the tinted premultiplied colour and the alpha is the plain product.
//
// `uAlpha` is the batch's own fade, and it scales the premultiplied output
// whole: a layer that dims everything it draws — the arrival veils the map,
// a piece of ambience fades in — writes one uniform instead of touching the
// alpha of every instance it packs. The default is 1, where the product is
// the shipping pixel.
export const QUAD_FRAGMENT = `#version 300 es
precision highp float;
uniform sampler2D uTex;
uniform float uAlpha;
in vec2 vUV;
in vec4 vColor;
out vec4 o;
void main() {
  vec4 t = texture(uTex, vUV);
  o = vec4(t.rgb * vColor.rgb, t.a * vColor.a) * uAlpha;
}`;

/** The batch's program, as both backends read it. */
export function quadProgram(): ProgramSource {
  const attribs = [CORNER_ATTRIB, ...QUAD_ATTRIBS];
  return {
    name: 'quads',
    glsl: { vertex: QUAD_VERTEX, fragment: QUAD_FRAGMENT },
    wgsl: quadModuleWgsl(QUAD_UNIFORMS, attribs),
    uniforms: QUAD_UNIFORMS,
    attribs,
  };
}

// ---------------------------------------------------------------------------
// The batch
// ---------------------------------------------------------------------------

/** What a quad batch needs: the batch half, and the atlas. */
export interface QuadBatchDeps extends BatchDeps {
  /** The atlas, bound on unit 0. */
  bindAtlas(unit: number): void;
}

export class QuadBatch extends InstanceBatch<QuadBatchDeps> {
  /**
   * The band scale of the four chart text steps (R1796), written as one
   * uniform on `flush`. 1 everywhere is the shipping pixel; only a chart
   * layer writes anything else.
   */
  readonly stepScale = new Float32Array([1, 1, 1, 1]);

  constructor(deps: QuadBatchDeps, capacity = 256) {
    super(deps, 'quads', QUAD_FLOATS, QUAD_ATTRIBS, capacity);
  }

  /**
   * A raw quad. `ox`/`oy` move the box centre off the pivot in world px and
   * rotate with the mark, which is how a bar sits above a hull.
   */
  quad(
    x: number, y: number, ox: number, oy: number, w: number, h: number,
    rot: number, flags: number, color: number, alpha: number,
    u0: number, v0: number, u1: number, v1: number,
  ): void {
    const at = this.buf.reserve();
    writeQuad(this.buf.data, at, x, y, ox, oy, w, h, rot, flags, u0, v0, u1, v1, color, alpha);
  }

  /**
   * One glyph of a chart word (R1796): a centred atlas sprite whose box
   * stands at `x, y` but whose offset is kept from the pivot `px, py`, in
   * the frame of the mark. A word's glyphs share one pivot, so the band
   * scale of the shader grows or shrinks the whole word about it.
   */
  glyph(
    px: number, py: number, x: number, y: number, rot: number, entry: AtlasEntry,
    tint: number, alpha: number, scale: number, flags: number,
  ): void {
    const at = this.buf.reserve();
    const dx = x - px, dy = y - py;
    const c = Math.cos(rot), s = Math.sin(rot);
    writeQuad(
      this.buf.data, at, px, py, dx * c + dy * s, dy * c - dx * s,
      entry.w * scale, entry.h * scale, rot, flags,
      entry.u0, entry.v0, entry.u1, entry.v1, tint, alpha,
    );
  }

  /**
   * An atlas sprite.
   *
   * `centred` picks the two placements the PixiJS client uses (the plan's
   * A.1): a hull, a bloom or a badge is anchored at its cropped-bounds
   * centre, and a site mark or a chevron sits at the path origin, which the
   * entry records as `ox`/`oy`.
   */
  sprite(
    x: number, y: number, rot: number, entry: AtlasEntry,
    tint: number, alpha: number, scale = 1, flags = 0, centred = true,
  ): void {
    const at = this.buf.reserve();
    writeQuad(
      this.buf.data, at, x, y,
      centred ? 0 : entry.ox * scale,
      centred ? 0 : entry.oy * scale,
      entry.w * scale, entry.h * scale, rot, flags,
      entry.u0, entry.v0, entry.u1, entry.v1, tint, alpha,
    );
  }

  protected setUniforms(p: Pipeline, cam: CameraUniforms): void {
    p.uniformMatrix3fv('uViewProj', cam.viewProj);
    p.uniform1f('uZoom', cam.zoom);
    p.uniform1f('uAlpha', this.globalAlpha);
    const st = this.stepScale;
    p.uniform4f('uStep', st[0], st[1], st[2], st[3]);
    this.deps.bindAtlas(0);
    p.sampler('uTex', 0);
  }
}
